import { useMemo } from 'react';
import { useCompanyInfoQuery } from '@/api/methods/useCompanyInfoQuery';

type FirstDayOffset = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface CalendarConfig {
  weeksOffset: number;
  firstDayOffset: number;
  timeZone: string;
  weekStartOptions: {
    weekStartsOn: FirstDayOffset;
    firstWeekContainsDate: number;
  };
}

const defaultCalendarConfig: CalendarConfig = {
  weeksOffset: 0,
  firstDayOffset: 1,
  timeZone: 'America/New_York',
  weekStartOptions: {
    weekStartsOn: 1,
    firstWeekContainsDate: 2,
  },
};

export const useCalendarConfig = (): CalendarConfig => {
  const { data } = useCompanyInfoQuery();

  return useMemo(() => {
    if (!data?.property) {
      return defaultCalendarConfig;
    }

    const timeZone = data.time_zone;
    const weeksOffset = data.property.week_offset;
    const firstDayOfWeek = data.property.first_day_of_week ?? 0;
    const firstDayOffset = formatFirstDayOffset(firstDayOfWeek + 1);
    /*
      date-fns weekStartsOn: 0 (Sunday) to 6 (Saturday)
      BE first_day_of_week: 0 (Monday) to 6 (Sunday)
      So we add 1 to first_day_of_week and convert 7 to 0
     */
    return {
      timeZone,
      weeksOffset,
      firstDayOffset,
      weekStartOptions: {
        weekStartsOn: firstDayOffset,
        firstWeekContainsDate: 7,
      },
    };
  }, [
    data?.time_zone,
    data?.property?.week_offset,
    data?.property?.first_day_of_week,
  ]);
};

const formatFirstDayOffset = (offset: number): FirstDayOffset => {
  if (offset === 7) {
    return 0;
  }

  return offset as FirstDayOffset;
};
